import {useEffect, useMemo, useState} from 'react'
import {Source, Layer} from '../MaptilerWrapper'
import PropTypes from 'prop-types'
import {useTheme} from '@mui/material/styles'
import {useConfig} from '../../../../../store/hooks/config'
import {formatPercentage, generateColorStops, getMapColors, getPaintProperty, statesToFIPS} from '../helpers'
import {useAppSelector} from '../../../../../store/hooks'
import {selectSelectedGradientView, selectSelectedPopulationViewMode} from '../../../../../store/slices/focusLayerSlice'

const StateTilesLayer = ({
  loadingAggregations,
  populationAggregationData,
  selectedFips,
  boundary,
  stateTilesetSourceLayer,
  visibility,
  stateFipsToPersona,
  personasToggle
}) => {
  const theme = useTheme()
  const mapColors = useMemo(() => getMapColors(theme), [theme])
  const {personasConfig} = useConfig()
  const selectedGradientView = useAppSelector(selectSelectedGradientView)
  const selectedPopulationViewMode = useAppSelector(selectSelectedPopulationViewMode)
  const [colors, setColors] = useState()
  const [colorsForStateTotal, setColorsForStateTotal] = useState()
  const [colorsForUsTotal, setColorsForUsTotal] = useState()

  const allStatesFips = useMemo(() => {
    return statesToFIPS.map((state) => state.code)
  }, [])

  // Memoize statewise aggregation for counts
  const statewiseAggregation = useMemo(
    () => {
      if (!loadingAggregations && populationAggregationData) {
        const statewiseData = populationAggregationData.map((state) => [state.fips, state.count])
        const missingFips = allStatesFips.filter((fips) => !statewiseData.some((item) => item[0] === fips))

        return [
          ...statewiseData,
          // Include states with missing data as zero count to ensure they are rendered on the map
          ...missingFips.map((fips) => [fips, 0])
        ]
      }
      return null
    },
    /* eslint-disable react-hooks/exhaustive-deps */
    [loadingAggregations, populationAggregationData]
  )

  // Mode: selected / total state population
  const aggForStateTotal = useMemo(() => {
    if (!loadingAggregations && populationAggregationData) {
      return populationAggregationData.map((state) => [state.fips, formatPercentage(state.statePercentage)])
    }
    return null
  }, [loadingAggregations, populationAggregationData])

  // Mode: selected / total US population
  const aggForUsTotal = useMemo(() => {
    if (!loadingAggregations && populationAggregationData) {
      return populationAggregationData.map((state) => [state.fips, formatPercentage(state.usPercentage)])
    }
    return null
  }, [loadingAggregations, populationAggregationData])

  useEffect(() => {
    if (statewiseAggregation) {
      setColors(generateColorStops(statewiseAggregation, mapColors))
    }
  }, [statewiseAggregation, mapColors])

  useEffect(() => {
    if (aggForStateTotal?.length > 0) {
      setColorsForStateTotal(generateColorStops(aggForStateTotal, mapColors, true))
    }
  }, [aggForStateTotal, mapColors])

  useEffect(() => {
    if (aggForUsTotal?.length > 0) {
      setColorsForUsTotal(generateColorStops(aggForUsTotal, mapColors, true))
    }
  }, [aggForUsTotal, mapColors])

  const layerType = personasToggle ? 'personas' : 'populationDensity'

  const colorsMap = (() => {
    if (selectedGradientView === 'population') return colors
    if (selectedPopulationViewMode === 'selected_of_us_total') return colorsForUsTotal
    return colorsForStateTotal
  })()

  const aggregationMap = (() => {
    if (selectedGradientView === 'population') return statewiseAggregation
    if (selectedPopulationViewMode === 'selected_of_us_total') return aggForUsTotal
    return aggForStateTotal
  })()
  return (
    <Source
      id='states'
      type='vector'
      url={'https://api.maptiler.com/tiles/01960ecc-5ea3-7e9c-89fc-02f6ee490715/tiles.json'}
      promoteId='GEOID'
    >
      {boundary === 'state' && statewiseAggregation && colorsMap && (
        <Layer
          beforeId='state-border'
          id='state-fill'
          type='fill'
          source='states'
          source-layer={stateTilesetSourceLayer}
          filter={['in', ['get', 'GEOID'], ['literal', selectedFips]]}
          paint={getPaintProperty(layerType, stateFipsToPersona, personasConfig, aggregationMap, colorsMap)}
          layout={{visibility}}
        />
      )}
      <Layer
        id='state-border'
        type='line'
        beforeId='State labels'
        source='states'
        source-layer={stateTilesetSourceLayer}
        paint={{
          'line-color': theme.palette.hpWhite,
          'line-width': 3,
          'line-opacity': 1
        }}
      />
      <Layer
        id='state-border-onhover'
        type='line'
        source='states'
        source-layer={stateTilesetSourceLayer}
        paint={{
          'line-color': theme.palette.hpBlack,
          'line-width': 2,
          'line-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 1, 0]
        }}
      />
      <Layer
        id='state-border-onhover-stroke'
        type='line'
        source='states'
        source-layer={stateTilesetSourceLayer}
        paint={{
          'line-color': theme.palette.hpWhite,
          'line-width': 2,
          'line-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 1, 0],
          'line-gap-width': 2
        }}
      />
    </Source>
  )
}

StateTilesLayer.propTypes = {
  loadingAggregations: PropTypes.bool,
  populationAggregationData: PropTypes.array,
  selectedFips: PropTypes.array,
  boundary: PropTypes.string,
  stateTilesetSourceLayer: PropTypes.string,
  visibility: PropTypes.string,
  stateFipsToPersona: PropTypes.object,
  personasToggle: PropTypes.bool
}

export default StateTilesLayer

/**
 * List of functions and their purpose:
 *
 * - StateTilesLayer: Renders state-level tiles with dynamic colors and borders based on population or personas.
 * - getPaintProperty: Determines the paint properties for the state tiles based on personas or population data.
 * - generateColorStops: Generates color stops for the heatmap based on population aggregation data.
 */
